<!--

 -->

<template>
  <oxd-form class="gsl-hrm-paper-container">
    <div class="gsl-hrm-timesheet-header">
      <div class="gsl-hrm-timesheet-header--title">
        <slot name="header-title"></slot>
      </div>
      <div class="gsl-hrm-timesheet-header--options">
        <slot name="header-options"></slot>
      </div>
    </div>

    <div v-if="loading" class="gsl-hrm-timesheet-loader">
      <oxd-loading-spinner />
    </div>
    <div
      v-else-if="!loading && (employeeJoinDate === '' || !employeeJoinDate)"
      class="gsl-hrm-timesheet-body-message"
    >
      <oxd-alert
        type="warn"
        :show="true"
        :message="$t('time.you_cannot_create_timesheet_without_joined_date')"
      ></oxd-alert>
    </div>
    <div
      v-else-if="!loading && checkIsBeforeJoinedDate()"
      class="gsl-hrm-timesheet-body-message"
    >
      <oxd-alert
        type="warn"
        :show="true"
        :message="$t('time.you_cannot_create_timesheet_joined_after_this_week')"
      ></oxd-alert>
    </div>
    <div
      v-else-if="!loading && !columns"
      class="gsl-hrm-timesheet-body-message"
    >
      <oxd-alert
        type="warn"
        :show="true"
        :message="$t('time.no_timesheets_found')"
      ></oxd-alert>
    </div>

    <div v-else class="gsl-hrm-timesheet-body">
      <table :class="tableClasses">
        <thead class="gsl-hrm-timesheet-table-header">
          <tr class="gsl-hrm-timesheet-table-header-row">
            <th :class="fixedColumnClasses">
              {{ $t('time.project') }}
            </th>
            <th class="gsl-hrm-timesheet-table-header-cell">
              {{ $t('time.activity') }}
            </th>

            <!-- timesheet days of week -->
            <th
              v-for="day in daysOfWeek"
              :key="day.id"
              class="gsl-hrm-timesheet-table-header-cell --center"
            >
              <span class="--day"> {{ day.day }} </span>
              <span>
                {{ day.title }}
              </span>
            </th>
            <!-- timesheet days of week -->

            <th
              v-if="!editable"
              class="gsl-hrm-timesheet-table-header-cell --center --freeze-right"
            >
              {{ $t('general.total') }}
            </th>
          </tr>
        </thead>

        <tbody class="gsl-hrm-timesheet-table-body">
          <!-- timesheet activities -->
          <tr
            v-for="(record, i) in records"
            :key="`${record.project?.id}-${record.activity?.id}-${i}`"
            class="gsl-hrm-timesheet-table-body-row"
          >
            <td :class="fixedCellClasses">
              <project-dropdown
                v-if="editable"
                :rules="rules.project"
                :exclude-project-ids="excludedProjectList"
                :model-value="getProject(record)"
                @update:model-value="updateProject($event, i)"
              />
              <span v-else>
                {{
                  record.project
                    ? `${record.customer.name} - ${record.project.name}`
                    : ''
                }}
              </span>
            </td>
            <td class="gsl-hrm-timesheet-table-body-cell">
              <activity-dropdown
                v-if="editable"
                :rules="rules.activity"
                :project-id="record.project && record.project.id"
                :model-value="getActivity(record.activity)"
                @update:model-value="updateActivity($event, i)"
              />
              <span v-else>{{ record.activity && record.activity.name }}</span>
              <br />
              <span>{{ 'Unit: ' + record?.activity?.unit }}</span>
            </td>
            <td
              v-for="(column, date) in columns"
              :key="`${record.project}_${record.activity}_${date}`"
              :class="{
                'gsl-hrm-timesheet-table-body-cell': true,
                '--center': true,
                '--duration-input': editable,
                '--highlight-3': !editable && column.workday,
                '--holiday-column': columns[date]?.holiday?.name,
                '--leave-column': columns[date]?.leave,
                '--weekend-column': columns[date]?.nonWorkingDay === 'fullDay',
                '--weekend-half-column':
                  columns[date]?.nonWorkingDay === 'halfDay',
              }"
            >
              <oxd-icon-button
                v-show="isCommentVisible(record.dates[date], i, date)"
                display-type="secondary"
                class="gsl-hrm-timesheet-icon-comment"
                :name="getCommentIcon(record.dates[date])"
                @mousedown="viewComment(record, record.dates[date], i, date)"
              />
              <oxd-input-field
                v-if="editable"
                autocomplete="off"
                :rules="validateDuration(date, i)"
                :model-value="getDuration(record.dates[date])"
                :placeholder="`${$t('general.duration')} (HH:MM)`"
                :disabled="checkIfDisable(date)"
                @blur="onDurationBlur"
                @focus="onDurationFocus(i, date)"
                @update:model-value="updateTime($event, i, date)"
              />
              <span v-else>
                <span
                  v-if="columns[date]?.holiday?.name"
                  class="--bold --amber"
                >
                  {{
                    getDuration(record.dates[date]) ??
                    columns[date]?.holiday?.name
                  }}
                </span>
                <span v-else-if="columns[date]?.leave" class="--bold --blue">
                  {{
                    getDuration(record.dates[date]) ??
                    columns[date]?.leave?.leaveType
                  }}
                </span>
                <span v-else>
                  {{ getDuration(record.dates[date]) ?? '00:00' }}
                </span>
              </span>
              <oxd-input-field
                v-if="editable && enableQuantity"
                autocomplete="off"
                :rules="validateQuantity(date, i)"
                :model-value="getQuantity(record.dates[date])"
                :placeholder="`${$t('general.quantity')} (number)`"
                :disabled="checkIfDisable(date)"
                @blur="onQuantityBlur"
                @focus="onQuantityFocus(i, date)"
                @update:model-value="updateQuantity($event, i, date)"
              />
              <span v-else>
                <span
                  v-if="!columns[date]?.holiday?.name && !columns[date]?.leave"
                >
                  <br />
                  {{ getQuantity(record.dates[date]) ?? '0' }}
                </span>
                <span v-else class="--bold">
                  <br />
                  {{ getQuantity(record.dates[date]) ?? null }}
                </span>
              </span>
            </td>
            <td
              v-if="!editable"
              class="gsl-hrm-timesheet-table-body-cell --center --freeze-right --highlight"
            >
              {{ record.total.label }}
            </td>
            <td
              v-if="editable"
              class="gsl-hrm-timesheet-table-body-cell --flex"
            >
              <oxd-icon-button
                name="trash"
                class="gsl-hrm-timesheet-icon"
                @click="deleteRow(i)"
              />
            </td>
          </tr>
          <!-- timesheet activities -->

          <!-- totals -->
          <tr
            v-if="!editable && records.length > 0"
            class="gsl-hrm-timesheet-table-body-row --total"
          >
            <td
              class="gsl-hrm-timesheet-table-body-cell --freeze-left --highlight"
            >
              {{ $t('general.total') }}
            </td>
            <td></td>
            <!-- total per day -->
            <td
              v-for="date in columns"
              :key="`total-${date}`"
              class="gsl-hrm-timesheet-table-body-cell --center"
            >
              {{ date.total.label }}
            </td>
            <!-- total per day -->
            <td
              class="gsl-hrm-timesheet-table-body-cell --center --freeze-right --highlight-2"
            >
              {{ subtotal }}
            </td>
          </tr>
          <!-- totals -->

          <!-- add row -->
          <tr v-if="editable" class="gsl-hrm-timesheet-table-body-row">
            <td class="gsl-hrm-timesheet-table-body-cell --flex">
              <oxd-icon-button
                name="plus"
                class="gsl-hrm-timesheet-icon"
                @click="addRow"
              />
              <oxd-text type="subtitle-2">
                {{ $t('time.add_row') }}
              </oxd-text>
            </td>
          </tr>
          <!-- add row -->

          <tr
            v-if="records.length === 0"
            class="gsl-hrm-timesheet-table-body-row"
          >
            <td colspan="9" class="gsl-hrm-timesheet-table-body-cell">
              {{ $t('general.no_records_found') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="gsl-hrm-timesheet-footer">
      <div class="gsl-hrm-timesheet-footer--title">
        <oxd-text tag="p" class="gsl-hrm-footer-link-title">
          <oxd-text
            tag="span"
            class="gsl-hrm-footer-link"
            @click="onClickProjectAllocation()"
          >
            View project allocation
          </oxd-text>
        </oxd-text>
        <oxd-text tag="p" class="gsl-hrm-footer-link-title">
          <oxd-text
            tag="span"
            class="gsl-hrm-footer-link"
            @click="onClickAttendance()"
          >
            View attendance
          </oxd-text>
        </oxd-text>
        <br />
        <div class="gsl-hrm-footer-guide-container">
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="indicator leave"></span>
            {{ 'Approve Leave' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="indicator holiday"></span>
            {{ 'Holiday' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="indicator half-day"></span>
            {{ 'Half working day' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="indicator weekend"></span>
            {{ 'Weekend' }}
          </oxd-text>
        </div>

        <br />
        <slot name="footer-title"></slot>
      </div>
      <div class="gsl-hrm-timesheet-footer--options">
        <slot name="footer-options"></slot>
      </div>
    </div>

    <timesheet-comment-modal
      v-if="showCommentModal"
      :editable="editable"
      :data="commentModalState"
      :timesheet-id="timesheetId"
      @close="onCommentModalClose"
    ></timesheet-comment-modal>
  </oxd-form>
</template>

<script>
import {validSelection, numericOnly} from '@/core/util/validation/rules';
import {
  parseDate,
  parseTimeInSeconds,
  parseDateAny,
} from '@ohrm/core/util/helper/datefns';
import ActivityDropdown from '@/orangehrmTimePlugin/components/ActivityDropdown.vue';
// import ProjectAutocomplete from '@/orangehrmTimePlugin/components/ProjectAutocomplete.vue';
import ProjectDropdown from '@/orangehrmTimePlugin/components/ProjectDropdown.vue';
import TimesheetCommentModal from '@/orangehrmTimePlugin/components/TimesheetCommentModal.vue';
import {OxdAlert, OxdSpinner} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import {urlFor} from '@ohrm/core/util/helper/url';

export default {
  name: 'Timesheet',

  components: {
    'oxd-alert': OxdAlert,
    'oxd-loading-spinner': OxdSpinner,
    'activity-dropdown': ActivityDropdown,
    // 'project-autocomplete': ProjectAutocomplete,
    'project-dropdown': ProjectDropdown,
    'timesheet-comment-modal': TimesheetCommentModal,
  },

  props: {
    records: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Object,
      required: false,
      default: () => null,
    },
    subtotal: {
      type: [String, Number],
      required: false,
      default: null,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    enableQuantity: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    timesheetId: {
      type: Number,
      default: null,
    },
    excludedProjectList: {
      type: Array,
      default: () => [],
    },
    timesheetOwner: {
      type: Object,
      required: false,
      default: () => null,
    },
    employeeJoinDate: {
      type: String,
      required: false,
      default: '',
    },
    timesheetPeriod: {
      type: String,
      required: false,
      default: null,
    },
    employeeNumber: {
      type: [String, Number],
      default: '',
    },
  },

  emits: ['update:records'],

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/attendance/records/check-for-attendance`,
    );

    return {
      http,
    };
  },
  data() {
    return {
      focusedField: null,
      showCommentModal: false,
      commentModalState: null,
      rules: {
        project: [
          validSelection,
          (v) => v !== null || this.$t('time.select_a_project'),
        ],
        activity: [
          (v) => v !== null || this.$t('time.select_an_activity'),
          (v) =>
            this.records.filter((record) => record.activity?.id === v?.id)
              .length < 2 || this.$t('time.duplicate_record'),
        ],
      },
    };
  },
  computed: {
    days() {
      return this.columns ? Object.keys(this.columns) : [];
    },
    dailyTotals() {
      const totals = {};
      for (const date in this.columns) {
        totals[date] = this.records.reduce((acc, record) => {
          const duration = parseTimeInSeconds(record.dates[date]?.duration);
          return duration > 0 ? acc + duration : acc;
        }, 0);
      }
      return totals;
    },

    daysOfWeek() {
      const days = [
        this.$t('general.sun'),
        this.$t('general.mon'),
        this.$t('general.tue'),
        this.$t('general.wed'),
        this.$t('general.thu'),
        this.$t('general.fri'),
        this.$t('general.sat'),
      ];
      return this.days.map((day) => {
        const date = parseDate(day, 'yyyy-MM-dd');
        return {
          id: date.valueOf(),
          day: date.getDate(),
          title: days[date.getDay()],
        };
      });
    },
    tableClasses() {
      return {
        'gsl-hrm-timesheet-table': true,
        '--editable': this.editable,
      };
    },
    fixedColumnClasses() {
      return {
        'gsl-hrm-timesheet-table-header-cell': true,
        '--freeze-left': !this.editable,
      };
    },
    fixedCellClasses() {
      return {
        'gsl-hrm-timesheet-table-body-cell': true,
        '--freeze-left': !this.editable,
      };
    },
  },

  methods: {
    onClickProjectAllocation() {
      window.open(
        urlFor(
          `/pim/viewProjectDetails/empNumber/${
            this.timesheetOwner?.empNumber ?? this.employeeNumber
          }`,
        ),
        '_blank',
      );
    },
    onClickAttendance() {
      window.open(urlFor(`/attendance/viewMyAttendanceRecord`), '_blank');
    },
    deleteRow(index) {
      const updated = this.records.filter((_, i) => i !== index);
      this.syncRecords(updated);
      this.$nextTick().then(() => {
        if (updated.length === 0) this.addRow();
      });
    },
    checkTheDurationNotNull(date, index) {
      return parseTimeInSeconds(this.records[index]?.dates[date]?.duration) > 0;
    },
    checkTheQuantityNotNull(date, index) {
      return this.records[index]?.dates[date]?.quantity > 0;
    },
    checkTheProjectNeedAttendance(index) {
      return this.records[index]?.project?.attendance;
    },
    checkIfProjectIsFilled(index) {
      return this.records[index]?.project ? true : false;
    },
    getProjectIdOfIndex(index) {
      return this.records[index]?.project.id;
    },
    addRow() {
      const updated = [
        ...this.records,
        {
          project: null,
          activity: null,
          dates: {},
        },
      ];
      this.syncRecords(updated);
    },
    updateTime($value, index, date) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          const _date = {
            [date]: {
              date: date,
              duration: $value,
              quantity: record.dates[date]?.quantity,
              id: record.dates[date]?.id,
              comment: record.dates[date]?.comment,
            },
          };
          record.dates = {...record.dates, ..._date};
        }
        return record;
      });
      this.syncRecords(updated);
    },
    updateQuantity($value, index, date) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          const _date = {
            [date]: {
              date: date,
              duration: record.dates[date]?.duration,
              quantity: $value,
              id: record.dates[date]?.id,
              comment: record.dates[date]?.comment,
            },
          };
          record.dates = {...record.dates, ..._date};
        }
        return record;
      });
      this.syncRecords(updated);
    },
    updateComment(id, comment, index, date) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          const _date = {
            [date]: {
              id: id,
              date: date,
              comment: comment,
              duration: record.dates[date]?.duration,
              quantity: record.dates[date]?.quantity,
            },
          };
          record.dates = {...record.dates, ..._date};
        }
        return record;
      });
      this.syncRecords(updated);
    },
    updateProject($value, index) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          record.project = $value ? $value : null;
          record.customer = $value?._customer ? $value._customer : null;
        }
        return record;
      });
      this.updateActivity(null, index);
      this.syncRecords(updated);
    },
    updateActivity($value, index) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          record.activity = $value
            ? {id: $value.id, name: $value.label, unit: $value.unit}
            : null;
        }
        return record;
      });
      this.syncRecords(updated);
    },
    syncRecords(updated) {
      if (!this.editable) return;
      this.$emit('update:records', updated);
    },
    viewComment(record, entry, index, date) {
      if (record.project?.id && record.activity?.id) {
        this.commentModalState = {
          date,
          index,
          id: entry?.id,
          project: record.project,
          activity: record.activity,
          customer: record.customer,
        };
        this.showCommentModal = true;
      } else {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: this.$t('time.select_a_project_and_an_activity'),
        });
      }
    },
    onCommentModalClose($event) {
      if ($event) {
        const {id, comment} = $event;
        const {index, date} = this.commentModalState;
        this.updateComment(id, comment, index, date);
      }
      this.showCommentModal = false;
      this.commentModalState = null;
    },
    getProject(record) {
      const {project, customer} = record;
      if (project && project.label) {
        return project;
      }
      if (project && customer) {
        return {
          id: project.id,
          label: `${customer.name} - ${project.name}`,
        };
      }
      return null;
    },
    getActivity(activity) {
      return activity
        ? {id: activity.id, label: activity.name, unit: activity.unit}
        : null;
    },
    getDuration(entry) {
      // TODO: convert to format from user config
      return entry?.duration ? entry.duration : null;
    },
    getQuantity(entry) {
      return entry?.quantity ? entry.quantity.toString() : null;
    },
    getCommentIcon(entry) {
      return entry?.comment ? 'chat-dots-fill' : 'chat-dots';
    },
    isCommentVisible(entry, index, date) {
      if (entry?.comment) return true;
      if (this.editable) {
        return (
          this.focusedField &&
          this.focusedField.index === index &&
          this.focusedField.date === date
        );
      }
      return false;
    },
    checkIsBeforeJoinedDate() {
      const employeeJoinDate = new Date(this.employeeJoinDate);
      const startOfTimePeriod = this.timesheetPeriod?.split(' ')[0];

      const startDate = parseDateAny(startOfTimePeriod);

      const dayOfWeek = employeeJoinDate.getDay();
      const startOfWeek = new Date(employeeJoinDate);
      startOfWeek.setDate(employeeJoinDate.getDate() - dayOfWeek + 1);
      startOfWeek.setHours(0, 0, 0, 0);
      return startDate < startOfWeek;
    },
    checkIfDisable(date) {
      const employeeJoinDate = new Date(this.employeeJoinDate);
      const selectedDate = new Date(date);

      return selectedDate < employeeJoinDate;
    },
    onDurationFocus(index, date) {
      this.focusedField = {index, date};
    },
    onDurationBlur() {
      this.focusedField = null;
    },
    onQuantityFocus(index, date) {
      this.focusedField = {index, date};
    },
    onQuantityBlur() {
      this.focusedField = null;
    },
    validateDuration(date, index) {
      const validateFormat = (v) => {
        return (
          v === '' ||
          v === null ||
          parseTimeInSeconds(v) >= 0 ||
          this.$t('time.should_be_less_than_24_and_in_hh_mm_or_decimal_format')
        );
      };

      const validateTotal = () => {
        return this.dailyTotals[date] > 86400
          ? this.$t('time.total_should_be_less_than_24_hours')
          : true;
      };
      const validateDurationForQuantity = (v) => {
        if (this.checkTheQuantityNotNull(date, index)) {
          return parseTimeInSeconds(v) > 0
            ? true
            : this.$t('time.duration_should_not_be_empty_or_zero');
        } else {
          return true;
        }
      };
      const validatePunchInRequired = (v) => {
        if (parseTimeInSeconds(v) > 0) {
          if (this.checkTheProjectNeedAttendance(index)) {
            return new Promise((resolve) => {
              this.http
                .request({
                  method: 'GET',
                  params: {
                    date: date,
                    timesheetId: this.timesheetId,
                  },
                })
                .then((res) => {
                  const {data, error} = res.data;
                  if (error) {
                    return resolve(error.message);
                  }
                  return data.valid === true
                    ? resolve(true)
                    : resolve(this.$t('time.attendance_required_for_the_date'));
                });
            });
          } else {
            return true;
          }
        } else {
          return true;
        }
      };
      const validateProjectAllocation = (v) => {
        const httpGetAllocation = new APIService(
          window.appGlobal.baseUrl,
          `/api/v2/pim/employees/${this.timesheetOwner.empNumber}/screen/project/project-allocation`,
        );
        if (parseTimeInSeconds(v) > 0) {
          if (this.checkIfProjectIsFilled(index)) {
            return new Promise((resolve) => {
              httpGetAllocation
                .request({
                  method: 'GET',
                })
                .then((res) => {
                  const {data, error} = res.data;
                  if (error) {
                    return resolve(error.message);
                  }
                  const projectAllocated = data.some((item) => {
                    if (item.project.id === this.getProjectIdOfIndex(index)) {
                      const startDate = new Date(item.startDate);
                      const endDate = new Date(item.endDate);
                      return (
                        startDate <= new Date(date) && new Date(date) <= endDate
                      );
                    }
                  });
                  resolve(
                    projectAllocated
                      ? true
                      : this.$t(
                          'general.project_is_not_allocated_on_this_date',
                        ),
                  );
                });
            });
          } else {
            return true;
          }
        } else {
          return true;
        }
      };

      return [
        validateFormat,
        validateTotal,
        validateDurationForQuantity,
        validatePunchInRequired,
        validateProjectAllocation,
      ];
    },
    validateQuantity(date, index) {
      const validateFormat = (v) => {
        return v === '' || v === null || numericOnly(v);
      };
      const validateQuantityForDuration = (v) => {
        if (this.checkTheDurationNotNull(date, index)) {
          return v > 0
            ? true
            : this.$t('time.quantity_should_be_number_greater_than_zero');
        } else {
          return true;
        }
      };
      return [validateFormat, validateQuantityForDuration];
    },
  },
};
</script>
<style lang="scss" scoped>
.gsl-hrm-footer-link-title {
  font-size: 13px;
  margin-right: 10px;
  display: flex;
  align-items: center;
}
.gsl-hrm-footer-guide-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  @include oxd-respond-to('md') {
    flex-direction: row;
  }
}
.gsl-hrm-footer-link {
  color: $oxd-primary-one-color;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
</style>
<style src="./timesheet.scss" lang="scss" scoped></style>
